import {Button, Card, DataStatus, openDialog} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {isEmpty, isNil} from 'ramda';

import {TwilioNumber, useGetTwilioNumbersQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AttachLineToNumberForm} from './AttachLineToNumberForm';

interface UnattachedNumberRowProps extends RequiredTestIdProps {
  number: TwilioNumber;
  canWriteTelephonySettings: boolean;
}

function UnattachedNumberRow(props: UnattachedNumberRowProps) {
  const handleConnectToLine = () =>
    openDialog(
      <AttachLineToNumberForm
        numberSid={props.number.sid}
        data-testid={suffixTestId('attachLine', props)}
      />,
      {
        id: 'attachLineToNumberDialog',
        title: i18n.t('page.settings.telephony.connectToLineTitle'),
        size: 'small',
      }
    );

  return (
    <Card variant="inlineWhite" data-testid={suffixTestId('numberRow', props)}>
      <VStack spacing={3}>
        <HStack justify="space-between" align="center" spacing={2}>
          <Text size="base" alternative data-testid={suffixTestId('phoneNumber', props)}>
            {props.number.phoneNumber}
          </Text>
          <Show when={props.canWriteTelephonySettings}>
            <Button
              variant="link"
              title={i18n.t('page.settings.telephony.connectToLine')}
              onClick={handleConnectToLine}
              data-testid={suffixTestId('connectToLine', props)}
            />
          </Show>
        </HStack>
        <Text size="xSmall" color="tertiary" data-testid={suffixTestId('sid', props)}>
          {props.number.sid}
        </Text>
      </VStack>
    </Card>
  );
}

/**
 * A number Omnetic owns but no line answers. It is read from the provider rather than from our own
 * data, so this section is deliberately independent of the lines list: when the provider is
 * unreachable the lines above still render.
 */
export function TelephonyUnattachedNumbersCard(props: RequiredTestIdProps) {
  const {data: numbers, isLoading, isError} = useGetTwilioNumbersQuery();

  const [canWriteTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite'],
  });

  const unattachedNumbers = (numbers?.numbers ?? []).filter((number) => isNil(number.lineId));

  if (!isLoading && !isError && isEmpty(unattachedNumbers)) {
    return null;
  }

  return (
    <Card
      title={i18n.t('page.settings.telephony.unattachedNumbersCardTitle')}
      data-testid={suffixTestId('unattachedNumbers', props)}
    >
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        minHeight={20}
        data-testid={suffixTestId('unattachedNumbersStatus', props)}
      >
        <VStack spacing={3}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.telephony.unattachedNumbersDescription')}
          </Text>
          {unattachedNumbers.map((number) => (
            <UnattachedNumberRow
              key={number.sid}
              number={number}
              canWriteTelephonySettings={canWriteTelephonySettings}
              data-testid={suffixTestId(`unattachedNumber-[${number.sid}]`, props)}
            />
          ))}
        </VStack>
      </DataStatus>
    </Card>
  );
}
